import { defineStore } from 'pinia'

import {
    getDetailTrafficSource,
    updateTrafficSource,
    deleteTrafficSourceParameter,
    getTrafficSourceEventsAll,
    updateStatusTrafficSourcesParameter,
    sendTestRequest,
} from '@/api/call-tracking/traffic-sources/traffic-sources-routes'

import { parseParams, updateParams } from '@/helpers/destinations/destination-request-helper'
import { getKeys, getKeysFromXML } from '@/helpers/app-helper'

import { useDefaultStore } from '@/store/defaultStore'
import { usePermissionsStore } from '@/store/permissionsStore'
import { useLocalFilterTableStore } from '@/store/localFilterTableStore'
import { useDetailTrafficSourceFacebookStore } from '@/store/call-tracking/trafficSources/detailTrafficSourceFacebookStore'
import { useDetailTrafficSourceConversationFilterStore } from '@/store/call-tracking/trafficSources/detailTrafficSourceConversationFilterStore'

import { SETTINGS_HEADER_TABLE } from '@/constants/headersTable/settingsHeaderTable'
import { TRAFFIC_SOURCES_PARAMETERS_HEADERS, TRAFFIC_SOURCES_PARAMETERS_HEADERS_WITH_ACTIONS } from '@/constants/headersTable/trafficSourcesParametersHeadersTable'
import { FILTER_ACTIVE, FILTER_NAME, FILTER_URL_PARAM } from '@/constants/localFilters'
import { REQUEST_BODY_TYPES } from '@/constants/requestBodyTypes'

export const useDetailTrafficSourceStore = defineStore('detailTrafficSource', {
    state: () => ({
        slug: null,
        id: null,
        loading: false,
        updateLoading: false,
        actionLoading: false,
        testRequestLoading: false,

        formData: {
            name: null,
            event_id: 1,
            postback_url: null,
            request_type: null,
            request_params: [],
            request_headers: [],
            body_form_data: [],
            body_raw_data_params: [],
            request_body_type: REQUEST_BODY_TYPES.formData.id,
            body_raw_data: '',
        },

        rawDataStatus: {
            saved: true,
            valid: true,
            message: null,
            unsavedRawData: null,
        },

        customParams: [],

        integration: null,

        search: '',
        selected: [],
        events: [],
        parameters: [],
        postback_request_params: [],

        parametersFilters: [
            FILTER_NAME,
            FILTER_URL_PARAM,
            FILTER_ACTIVE,
        ],

        conversationFilterEditMode: false,
        conversationFilterUnchanged: true,
    }),

    getters: {
        headersByPermission() {
            const permissionsStore = usePermissionsStore()

            return permissionsStore.permissions.trafficSources.edit
                ? TRAFFIC_SOURCES_PARAMETERS_HEADERS_WITH_ACTIONS.map((head, index) => ({
                    ...head,
                    orderIndex: index,
                }))
                : TRAFFIC_SOURCES_PARAMETERS_HEADERS.map((head, index) => ({
                    ...head,
                    orderIndex: index,
                }))
        },
    },

    actions: {
        updateIntegration(integration) {
            this.integration = integration
        },

        getQueryLocalStorage() {
            const localFilterTableStore = useLocalFilterTableStore()
            localFilterTableStore.initializeTableSettings(SETTINGS_HEADER_TABLE.trafficSourceParameters, this.parametersFilters)
            localFilterTableStore.setHeaders(SETTINGS_HEADER_TABLE.trafficSourceParameters, this.headersByPermission)
            localFilterTableStore.getQueryLocalStorage(SETTINGS_HEADER_TABLE.trafficSourceParameters, this.id)
        },

        setTrafficSourceSlug(slug) {
            this.slug = slug
        },

        addParameter(newParameter) {
            this.parameters.push(newParameter)
        },

        _changeParametersAfterDelete(ids) {
            this.parameters = this.parameters.filter((i) => !ids.includes(i.id))
        },

        _changeManyParametersAfterUpdate(payload) {
            payload.forEach((item) => {
                const index = this.parameters.findIndex((i) => i.id === item.id)
                if (index !== -1) {
                    this.parameters.splice(index, 1, item)
                }
            })
        },

        parsePayload({ postback_request_params, ts }) {
            this.postback_request_params = parseParams(postback_request_params)
            this.parsePayloadTs(ts)
        },

        parsePayloadTs({
            integration, url_params = [], facebook_ads_customer_parameters, traffic_source_filter, ...other
        }, update = false) {
            this.id = other.id

            this.formData = {
                ...other,
                event_id: other.event_id ?? this.formData.event_id,
            }

            this.parameters = url_params

            this.integration = integration || null

            this.setFacebookAdsCustomerParameters(facebook_ads_customer_parameters)

            if (!update) {
                this.setCallFilterGroupsToStore(traffic_source_filter)
            }
        },

        setRawDataStatus({
            saved = true, valid = true, message = null, unsavedRawData = null,
        }) {
            this.rawDataStatus = {
                saved, valid, message, unsavedRawData,
            }
        },

        setCallFilterGroupsToStore(filter) {
            const detailTrafficSourceConversationFilterStore = useDetailTrafficSourceConversationFilterStore()

            if (!!filter && filter.hasOwnProperty('groups')) {
                detailTrafficSourceConversationFilterStore.setCallFilterGroups(filter.groups)
            } else {
                detailTrafficSourceConversationFilterStore.setCallFilterGroups([])
            }
        },

        setFacebookAdsCustomerParameters(params) {
            if (params) {
                const detailTrafficSourceFacebookStore = useDetailTrafficSourceFacebookStore()

                detailTrafficSourceFacebookStore.setFacebookAdsCustomerParameters(params)
            }
        },

        updateCustomParams() {
            this.postback_request_params = updateParams(this.postback_request_params)
        },

        changeSelectedItems(newSelectedItems) {
            this.selected = newSelectedItems
        },

        addConversion(conversion) {
            if (this.integration) {
                this.integration.conversions.push(conversion)
            }
        },

        async goToLoadDetailTrafficSource(withoutLoading = false) {
            const defaultStore = useDefaultStore()

            this.loading = !withoutLoading

            const { success, payload, message } = await getDetailTrafficSource(this.slug)

            if (success) {
                this.parsePayload(payload)
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.loading = false

            return success
        },

        async goToUpdateTrafficSource(formDataForUpdate) {
            const defaultStore = useDefaultStore()

            this.updateLoading = true

            const formDataForSend = {
                ...this.formData,
                ...formDataForUpdate,
            }

            const { success, payload, message } = await updateTrafficSource({
                formData: formDataForSend,
                slug: this.slug,
            })

            if (success) {
                this.parsePayloadTs({ ...formDataForSend, integration: payload.integration, url_params: this.parameters }, true)
                this.setRawDataStatus({})

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.updateLoading = false

            return { success }
        },

        async goToDeleteTrafficSourceParameter({ trafficSources = [], trafficSource = null }) {
            const defaultStore = useDefaultStore()

            this.actionLoading = true

            const formData = {}

            if (trafficSource) {
                formData.ids = [trafficSource.id]
            } else {
                formData.ids = trafficSources.map(({ id }) => id)
            }

            const { success, message } = await deleteTrafficSourceParameter({
                slug: this.slug,
                data: formData,
            })

            if (success) {
                this._changeParametersAfterDelete(formData.ids)
                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return success
        },

        clearSelected() {
            this.selected = []
        },

        async goToChangeStatusActiveTrafficSourceParameter({ trafficSource = null, trafficSources = [], status_id }) {
            const defaultStore = useDefaultStore()

            this.updateLoading = true

            const formData = {}

            if (trafficSource) {
                formData.ids = [trafficSource.id]
            } else {
                formData.ids = trafficSources.map(({ id }) => id)
            }
            formData.active = status_id

            const { success, payload, message } = await updateStatusTrafficSourcesParameter({
                slug: this.slug,
                formData,
            })

            if (success) {
                this._changeManyParametersAfterUpdate(payload)
                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.updateLoading = false

            return success
        },

        async goToLoadTrafficSourceEventsAll() {
            if (this.events.length) {
                return
            }

            const { success, payload, message } = await getTrafficSourceEventsAll()

            if (success) {
                this.events = payload
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }
        },

        async goToSendTestRequest(params) {
            const defaultStore = useDefaultStore()

            this.testRequestLoading = true

            const {
                success, message, payload = {}, status,
            } = await sendTestRequest({ slug: this.slug, params })

            if (success) {
                defaultStore.setSuccessMessage({ message })

                this._parseTestResponse(payload)
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.testRequestLoading = false

            return {
                success, payload, status, message,
            }
        },

        _parseTestResponse(payload) {
            if (payload.hasOwnProperty('xml') && payload.xml) {
                this._parseTestResponseXml(payload)
            } else {
                this._parseTestResponseJson(payload)
            }
        },

        _parseTestResponseXml(payload) {
            this.testResponse = getKeysFromXML(payload.data.documentElement)
        },

        _parseTestResponseJson(payload) {
            this.testResponse = getKeys(payload)
        },

        changeSearch(value) {
            this.search = value
        },

        changeConversationFilterEditMode(value) {
            this.conversationFilterEditMode = value
        },

        setConversationFilterUnchanged(value) {
            this.conversationFilterUnchanged = value
        },
    },
})
